/**
 * HUKUKİ METİNLER — Gizlilik & KVKK Aydınlatma Metni + Kullanım Koşulları.
 * Giriş gerektirmez (login ekranından ve hesap sayfasından linklenir).
 *
 * ⚠️ Bu metinler yayın öncesi HUKUKİ TASLAKTIR: yapı KVKK m.10 aydınlatma yükümlülüğüne
 * göre kurulmuştur ama yayına almadan önce bir hukukçu tarafından şirket bilgileriyle
 * (unvan, adres, e-posta) tamamlanıp onaylanmalıdır. Boşluklar [KÖŞELİ] işaretlidir.
 */
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

function Shell({ title, updated, children }: { title: string; updated: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="mx-auto max-w-3xl px-5 py-10">
        <Link href="/">
          <span className="mb-6 inline-flex cursor-pointer items-center gap-1.5 text-theme-sm text-brand-500 hover:underline">
            <ArrowLeft className="h-4 w-4" /> Geolop'a dön
          </span>
        </Link>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white/90">{title}</h1>
        <p className="mt-1 text-theme-sm text-gray-500">Son güncelleme: {updated}</p>
        <div className="prose-sm mt-8 space-y-5 text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-800 dark:[&_h2]:text-white/90 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
      </div>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <Shell title="Gizlilik Politikası ve KVKK Aydınlatma Metni" updated="2 Ağustos 2026">
      <p>
        Bu metin, 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında,
        Geolop platformunu ("Platform") kullanan kişilerin kişisel verilerinin nasıl
        işlendiğini açıklar. Veri sorumlusu: <strong>[ŞİRKET UNVANI — doldurulacak]</strong>,
        iletişim: <strong>[E-POSTA / ADRES — doldurulacak]</strong>.
      </p>

      <h2>1. İşlenen Kişisel Veriler</h2>
      <ul>
        <li><strong>Hesap verileri:</strong> ad, e-posta adresi, şifre (geri döndürülemez özetlenmiş olarak — düz metin şifre saklanmaz).</li>
        <li><strong>Kullanım verileri:</strong> platform içi işlem kayıtları, analiz çalıştırma geçmişi ve kaynak tüketimi (token kullanımı).</li>
        <li><strong>Site analiz verileri:</strong> Platforma eklediğiniz internet sitelerine ait, kamuya açık kaynaklardan toplanan teknik ve içerik verileri.</li>
        <li><strong>Bağlantı verileri:</strong> Google hesabınızı bağlamanız hâlinde, yalnızca yetkilendirdiğiniz kapsamla sınırlı arama ve trafik ölçüm verileri ile erişim anahtarları.</li>
        <li><strong>Teknik anahtarlar:</strong> kendi hizmet anahtarlarınızı girmeniz hâlinde bunlar veritabanında şifreli (AES-256-GCM) saklanır ve arayüzde yalnızca maskeli gösterilir.</li>
      </ul>

      <h2>2. İşleme Amaçları ve Hukuki Sebep</h2>
      <ul>
        <li>Hizmetin sunulması, hesabın yönetilmesi ve analizlerin üretilmesi (KVKK m.5/2-c: sözleşmenin ifası).</li>
        <li>Hizmet bildirimleri ve güvenlik uyarılarının e-postayla iletilmesi (m.5/2-f: meşru menfaat).</li>
        <li>Yasal yükümlülüklerin yerine getirilmesi (m.5/2-ç).</li>
      </ul>

      <h2>3. Aktarım</h2>
      <p>
        Verileriniz; barındırma/veritabanı altyapısı, arama verisi sağlayıcıları, yapay zekâ
        model sağlayıcıları ve e-posta iletim altyapısı gibi hizmetin çalışması için zorunlu
        yurt içi/yurt dışı işleyicilere, yalnızca hizmetin gerektirdiği ölçüde aktarılır.
        Verileriniz üçüncü kişilere satılmaz ve reklam amaçlı paylaşılmaz.
      </p>

      <h2>4. Saklama Süresi</h2>
      <p>
        Hesap verileri üyelik süresince; analiz kayıtları hizmet geçmişinizin görüntülenebilmesi
        için hesap silinene kadar saklanır. Hesabınızı sildiğinizde tüm verileriniz geri
        dönüşsüz olarak silinir (yasal saklama yükümlülüğü doğuran kayıtlar hariç).
      </p>

      <h2>5. Haklarınız (KVKK m.11)</h2>
      <ul>
        <li>Verilerinizin işlenip işlenmediğini öğrenme ve bilgi talep etme,</li>
        <li>Eksik/yanlış işlenen verilerin düzeltilmesini isteme,</li>
        <li>Silinmesini veya yok edilmesini isteme (Platform içinde <strong>Hesabım → Hesabı Sil</strong> ile anında kullanılabilir),</li>
        <li>İşlemeye itiraz etme ve zarara uğramanız hâlinde giderim talep etme.</li>
      </ul>
      <p>Talepler için: <strong>[İLETİŞİM E-POSTASI — doldurulacak]</strong>. Başvurular en geç 30 gün içinde yanıtlanır.</p>

      <h2>6. Çerezler ve Yerel Depolama</h2>
      <p>
        Platform, oturumunuzu sürdürmek için zorunlu nitelikte yerel depolama kullanır.
        Reklam veya üçüncü taraf takip çerezi kullanılmaz.
      </p>
    </Shell>
  );
}

export function TermsPage() {
  return (
    <Shell title="Kullanım Koşulları" updated="2 Ağustos 2026">
      <p>
        Bu koşullar, Geolop platformunun kullanımını düzenler. Hesap oluşturarak bu koşulları
        kabul etmiş sayılırsınız. Hizmet sağlayıcı: <strong>[ŞİRKET UNVANI — doldurulacak]</strong>.
      </p>

      <h2>1. Hizmetin Kapsamı</h2>
      <p>
        Geolop; internet sitelerinin arama ve yapay zekâ görünürlüğünü analiz eden, içerik ve
        teknik öneriler üreten bir yazılım hizmetidir (SaaS). Analiz çıktıları veri
        sağlayıcılarından ve modellemeden elde edilen <strong>tahmin ve önerilerdir</strong>;
        belirli bir sıralama veya trafik sonucu garanti edilmez.
      </p>

      <h2>2. Hesap ve Paketler</h2>
      <ul>
        <li>Hesabınızın güvenliğinden ve hesabınız altında yapılan işlemlerden siz sorumlusunuz.</li>
        <li>Kullanım hakları paketinize tanımlı <strong>token</strong> ve site limitleriyle sınırlıdır; limit dolduğunda ücretli işlemler durur ve bilgilendirilirsiniz.</li>
        <li>Yalnızca sahibi olduğunuz veya analiz etme yetkiniz bulunan siteleri eklemelisiniz.</li>
      </ul>

      <h2>3. Kabul Edilebilir Kullanım</h2>
      <ul>
        <li>Platform; hukuka aykırı içerik yaymak, üçüncü kişilerin sistemlerine zarar vermek veya yetkisiz erişim için kullanılamaz.</li>
        <li>Platformun çıktıları müşterilerinize sunulabilir; ancak Platformun kendisi izinsiz yeniden satılamaz.</li>
      </ul>

      <h2>4. Ücretlendirme ve İptal</h2>
      <p>
        Paket ücretleri ve kapsamları satın alma sırasında açıkça gösterilir. Aboneliğinizi
        <strong> Hesabım</strong> sayfasından istediğiniz an durdurabilirsiniz; dönem sonuna kadar
        hizmet açık kalır. İade koşulları: <strong>[İADE POLİTİKASI — doldurulacak]</strong>.
      </p>

      <h2>5. Sorumluluk Sınırı</h2>
      <p>
        Hizmet "olduğu gibi" sunulur. Dolaylı zararlar, veri sağlayıcılarından kaynaklanan
        kesinti ve doğruluk sorunları ile üçüncü taraf platform politikalarındaki değişikliklerden
        doğan sonuçlardan Platform sorumlu tutulamaz. Toplam sorumluluk, son 3 ayda ödenen
        ücretle sınırlıdır.
      </p>

      <h2>6. Değişiklikler</h2>
      <p>Koşullar güncellenebilir; önemli değişiklikler e-postayla duyurulur.</p>
    </Shell>
  );
}
